import { Controller } from "@hotwired/stimulus"
import { DirectUploadsController } from "@rails/activestorage"

// Sends the chosen files straight to the storage bucket before the form is submitted, so the
// form itself only posts their signed ids. Posted as multipart, a large file (a DATANORM
// delivery may be 500 MB) streams through the app server for minutes, and a connection that
// drops on the way loses the whole upload — Turbo only rethrows "Failed to fetch".
//
// There is no global ActiveStorage.start(), so `direct_upload: true` on a field does nothing by
// itself: this runs Active Storage's own DirectUploadsController on submit. Each upload puts a
// hidden input named like its file field next to it when it starts and fills in the blob's
// signed id when it finishes; the file field is disabled meanwhile, so the files are not posted a
// second time, and the form is submitted once all are in.
export default class extends Controller {
  static targets = ["submit", "progress", "error"]
  static values = { progressMessage: String, errorMessage: String, leaveMessage: String }

  // Also runs when Turbo restores the page from its cache, which holds the disabled field and
  // the signed ids of the upload that already went through.
  connect() {
    this.fileInputs.forEach((input) => {
      input.disabled = false
      this.signedIdInputs(input).forEach((hidden) => hidden.remove())
    })
    this.toggleBusy(false)
  }

  disconnect() {
    this.uploading = false
  }

  upload(event) {
    // Before looking for files: the running upload disabled the file fields, so a second submit
    // would find none and go through, posting the still empty hidden inputs.
    if (this.uploading) return event.preventDefault()

    const uploads = new DirectUploadsController(this.element)
    if (uploads.inputs.length === 0) return

    event.preventDefault()
    this.uploading = true
    this.toggleBusy(true)
    this.showError("")
    uploads.inputs.forEach((input) => {
      this.signedIdInputs(input).forEach((hidden) => hidden.remove())
      input.disabled = true
    })

    uploads.start((error) => {
      this.uploading = false
      if (error) {
        // The uploads that did finish would otherwise ride along with the next submit.
        uploads.inputs.forEach((input) => {
          input.disabled = false
          this.signedIdInputs(input).forEach((hidden) => hidden.remove())
        })
        this.toggleBusy(false)
        this.showError(this.errorMessageValue)
        return
      }

      // requestSubmit() refuses a disabled submitter.
      if (!this.hasSubmitTarget) return this.element.requestSubmit()

      this.submitTarget.disabled = false
      this.element.requestSubmit(this.submitTarget)
    })
  }

  progress(event) {
    if (!this.hasProgressTarget) return

    const { file, progress } = event.detail
    this.progressTarget.textContent = this.progressMessageValue
      .replace("%{filename}", file.name)
      .replace("%{percent}", Math.round(progress))
  }

  // Leaving the page aborts the upload; the browser asks on reload or close, Turbo on a link.
  confirmUnload(event) {
    if (!this.uploading) return

    event.preventDefault()
    event.returnValue = ""
  }

  confirmVisit(event) {
    if (this.uploading && !window.confirm(this.leaveMessageValue)) event.preventDefault()
  }

  // Active Storage alert()s an upload error unless the event is cancelled; the form shows it.
  failed(event) {
    event.preventDefault()
  }

  toggleBusy(busy) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = busy
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = ""
      this.progressTarget.classList.toggle("hidden", !busy)
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("hidden", !message)
  }

  signedIdInputs(input) {
    return Array.from(this.element.querySelectorAll("input[type=hidden]")).filter(
      (hidden) => hidden.name === input.name,
    )
  }

  get fileInputs() {
    return Array.from(this.element.querySelectorAll("input[type=file][data-direct-upload-url]"))
  }
}
